/**
 * API Routes - Single Source of Truth (SSOT)
 *
 * Centralized definition of all REST API routes.
 * This file is the ONLY place where API URLs should be defined.
 *
 * IMPORTANT FOR AI ASSISTANTS:
 * When adding new API methods to api.ts or auth-api.ts:
 * 1. FIRST add the route definition here
 * 2. THEN import and use it in the API client
 * 3. NEVER hardcode URL strings directly in api.ts
 *
 * Route naming convention:
 * - list: GET collection (e.g., /protocols)
 * - detail: GET single item (e.g., /protocols/{id})
 * - create: POST to collection (same as list URL)
 * - update: PUT to single item (same as detail URL)
 * - delete: DELETE single item (same as detail URL)
 *
 * @see src/API/Endpoints/ for PHP implementation
 * @see CLAUDE.md for documentation
 */

import type { TagEntityType } from '@/types/tag';
// Import CodebookType from SSOT and re-export for backward compatibility
import type { CodebookType } from '@/components/codebooks/codebook-config';
export type { CodebookType };

// ============================================================================
// PROTOCOLS
// @see src/API/Endpoints/ProtocolsEndpoint.php
// ============================================================================

export const PROTOCOL_ROUTES = {
	/** GET /protocols - list, POST /protocols - create */
	list: '/protocols',
	/** POST /protocols/plan - quick calendar planning */
	plan: '/protocols/plan',
	/** GET /protocols/{id} */
	detail: (id: number) => `/protocols/${id}`,
	/** GET /protocols/{id}/events */
	events: (id: number) => `/protocols/${id}/events`,
	/** GET /protocols/{id}/email-options */
	emailOptions: (id: number) => `/protocols/${id}/email-options`,
	/** POST /protocols/{id}/resend-email */
	resendEmail: (id: number) => `/protocols/${id}/resend-email`,
	/** GET /protocols/{id}/download-pdf */
	downloadPdf: (id: number) => `/protocols/${id}/download-pdf`,
	/** POST /protocols/{id}/share-pdf */
	sharePdf: (id: number) => `/protocols/${id}/share-pdf`,
	/** POST /protocols/{id}/transition - workflow state transition */
	transition: (id: number) => `/protocols/${id}/transition`,
	/** POST /protocols/{id}/lock - acquire edit lock */
	acquireLock: (id: number) => `/protocols/${id}/lock`,
	/** DELETE /protocols/{id}/lock - release edit lock */
	releaseLock: (id: number) => `/protocols/${id}/lock`,
} as const;

// ============================================================================
// CLIENTS
// @see src/API/Endpoints/ClientsEndpoint.php
// ============================================================================

export const CLIENT_ROUTES = {
	/** GET /clients - list, POST /clients - create */
	list: '/clients',
	/** GET /clients/{id}, PUT /clients/{id} */
	detail: (id: number) => `/clients/${id}`,
	/** GET /clients/nearby?lat=X&lng=Y - find by GPS */
	nearby: '/clients/nearby',

	// Client Portal endpoints
	/** GET /clients/{id}/user-status - get portal user status */
	userStatus: (id: number) => `/clients/${id}/user-status`,
	/** POST /clients/{id}/invite - invite client to portal */
	invite: (id: number) => `/clients/${id}/invite`,
	/** POST /clients/{id}/assign-user - assign existing WP user */
	assignUser: (id: number) => `/clients/${id}/assign-user`,
	/** DELETE /clients/{id}/user - disconnect user from client (M:N - optional user_id param) */
	disconnectUser: (id: number, userId?: number) =>
		userId ? `/clients/${id}/user?user_id=${userId}` : `/clients/${id}/user`,
} as const;

// ============================================================================
// CLIENT ADDRESSES
// @see src/API/Endpoints/ClientAddressesEndpoint.php
// ============================================================================

export const CLIENT_ADDRESS_ROUTES = {
	/** GET /clients/{clientId}/addresses, POST - create */
	list: (clientId: number) => `/clients/${clientId}/addresses`,
	/** GET, PUT, DELETE /clients/{clientId}/addresses/{id} */
	detail: (clientId: number, addressId: number) => `/clients/${clientId}/addresses/${addressId}`,
	/** PUT /clients/{clientId}/addresses/{id}/set-default */
	setDefault: (clientId: number, addressId: number) =>
		`/clients/${clientId}/addresses/${addressId}/set-default`,
} as const;

// ============================================================================
// FORM DATA (Read-only, active items only)
// @see src/API/Endpoints/MiscEndpoint.php
//
// These return ONLY ACTIVE items for use in ProtocolForm dropdowns.
// For admin CRUD operations, use CODEBOOK_ROUTES instead.
// ============================================================================

export const FORM_DATA_ROUTES = {
	/** GET /materials - active materials for ProtocolForm */
	materials: '/materials',
	/** GET /installation-types - active installation types for ProtocolForm */
	installationTypes: '/installation-types',
	/** GET /pests - active pests for ProtocolForm */
	pests: '/pests',
	/** GET /units - active units for ProtocolForm */
	units: '/units',
} as const;

// ============================================================================
// PDF
// @see src/API/Endpoints/MiscEndpoint.php
// ============================================================================

export const PDF_ROUTES = {
	/** POST /pdf/preview - generate PDF preview (developer mode) */
	preview: '/pdf/preview',
} as const;

// ============================================================================
// PHOTOS
// @see src/API/Endpoints/PhotosEndpoint.php
// ============================================================================

export const PHOTO_ROUTES = {
	/** POST /photos/upload */
	upload: '/photos/upload',
	/** POST /photos/finalize */
	finalize: '/photos/finalize',
	/** DELETE /photos/{filename} */
	delete: (filename: string) => `/photos/${encodeURIComponent(filename)}`,
} as const;

// ============================================================================
// CODEBOOKS (Admin CRUD - includes inactive items)
// @see src/API/Endpoints/CodebooksEndpoint.php
//
// ⚠️ IMPORTANT: ALL codebook operations use /codebooks/{type} prefix!
// There are NO separate endpoints like /work-types or /work-subtypes.
//
// For read-only access (active items only), use FORM_DATA_ROUTES.
// ============================================================================

/**
 * Query parameter that unlocks codebook pricing fields (DESIGN §3.1).
 * Pricing reads must use a distinct URL so they never share the shared SW cache
 * entry with the default (price-less) read — see lib/sw-routes.ts.
 */
export const PRICING_QUERY_PARAM = 'include_pricing';

/** Append ?include_pricing=1 to a codebook route when pricing is requested. */
export function withPricing(route: string, includePricing?: boolean): string {
	return includePricing ? `${route}?${PRICING_QUERY_PARAM}=1` : route;
}

export const CODEBOOK_ROUTES = {
	/** GET /codebooks/{type} - list all, POST - create */
	list: (type: CodebookType) => `/codebooks/${type}`,
	/** GET, PUT, DELETE /codebooks/{type}/{id} */
	detail: (type: CodebookType, id: number) => `/codebooks/${type}/${id}`,
	/** POST /codebooks/{type}/{id}/toggle - toggle is_active */
	toggle: (type: CodebookType, id: number) => `/codebooks/${type}/${id}/toggle`,
	/** POST /codebooks/{type}/reorder - bulk reorder items */
	reorder: (type: CodebookType) => `/codebooks/${type}/reorder`,
	/** POST /codebooks/recommendations/{id}/duplicate */
	duplicateRecommendation: (id: number) => `/codebooks/recommendations/${id}/duplicate`,
} as const;

// ============================================================================
// RECOMMENDATIONS
// @see src/API/Endpoints/RecommendationsEndpoint.php
// ============================================================================

export const RECOMMENDATION_ROUTES = {
	/** GET /recommendations/active - all active recommendations with scopes (for offline caching) */
	active: '/recommendations/active',
	/**
	 * GET /recommendations/filtered - filtered for ProtocolForm
	 * @deprecated Use active endpoint with client-side filtering for offline support
	 */
	filtered: '/recommendations/filtered',
	/** POST /recommendations/upload-image */
	uploadImage: '/recommendations/upload-image',
	/** DELETE /recommendations/image/{filename} */
	deleteImage: (filename: string) => `/recommendations/image/${encodeURIComponent(filename)}`,
} as const;

// ============================================================================
// USERS
// @see src/API/Endpoints/UsersEndpoint.php
// ============================================================================

export const USER_ROUTES = {
	/** GET /users - list, POST /users - create */
	list: '/users',
	/** GET, PUT, DELETE /users/{id} */
	detail: (id: number) => `/users/${id}`,
	/** POST /users/{id}/reset-password */
	resetPassword: (id: number) => `/users/${id}/reset-password`,
	/** POST /user/locale - set current user's locale preference */
	setLocale: '/user/locale',
} as const;

// ============================================================================
// CLIENT USERS (ddd_client role management from UsersPage)
// @see src/API/Endpoints/ClientUserEndpoint.php
// ============================================================================

export const CLIENT_USER_ROUTES = {
	/** POST /client-users - create client user with multiple clients */
	create: '/client-users',
	/** PUT /client-users/{id} - update client user (name + clients) */
	update: (id: number) => `/client-users/${id}`,
	/** DELETE /client-users/{id} - disconnect from all clients + cleanup */
	delete: (id: number) => `/client-users/${id}`,
} as const;

// ============================================================================
// ACTIVITY LOGS
// @see src/API/Endpoints/ActivityLogsEndpoint.php
// ============================================================================

export const ACTIVITY_LOG_ROUTES = {
	/** GET /activity-logs */
	list: '/activity-logs',
	/** GET /activity-logs/{id} */
	detail: (id: number) => `/activity-logs/${id}`,
	/** GET /activity-logs/filters */
	filters: '/activity-logs/filters',
} as const;

// ============================================================================
// SYNC LOG
// @see src/API/Endpoints/SyncLogEndpoint.php
// ============================================================================

export const SYNC_LOG_ROUTES = {
	/** POST /sync-log */
	log: '/sync-log',
} as const;

// ============================================================================
// SYSTEM
// @see src/API/Endpoints/SystemEndpoint.php
// ============================================================================

export const SYSTEM_ROUTES = {
	/** GET /system/version - get plugin version (public, for update check) */
	version: '/system/version',
	/** GET /system/check-update - check for available updates */
	checkUpdate: '/system/check-update',
	/** GET /system/health - system health check */
	health: '/system/health',
	/** GET /system/security-audit - security audit (admin only) */
	securityAudit: '/system/security-audit',
	/** POST /system/run-update - execute plugin update */
	runUpdate: '/system/run-update',
	/** POST /system/run-migration - manually run database migrations */
	runMigration: '/system/run-migration',
	/** POST /system/seed-recommendations - run system recommendation seed */
	seedRecommendations: '/system/seed-recommendations',
} as const;

// ============================================================================
// FEEDBACK
// @see src/API/Endpoints/FeedbackEndpoint.php
// ============================================================================

export const FEEDBACK_ROUTES = {
	/** POST /feedback/email - forward Sentry feedback to support email */
	submit: '/feedback/email',
} as const;

// ============================================================================
// AUTH
// @see src/API/Endpoints/AuthEndpoint.php
// ============================================================================

export const AUTH_ROUTES = {
	/** POST /auth/login */
	login: '/auth/login',
	/** POST /auth/logout */
	logout: '/auth/logout',
	/** GET /auth/check */
	check: '/auth/check',
	/** POST /auth/forgot-password */
	forgotPassword: '/auth/forgot-password',
	/** POST /auth/validate-reset-key */
	validateResetKey: '/auth/validate-reset-key',
	/** POST /auth/set-password */
	setPassword: '/auth/set-password',
} as const;

// ============================================================================
// SETTINGS
// @see src/API/Endpoints/SettingsEndpoint.php
// ============================================================================

export const SETTINGS_ROUTES = {
	/** GET /settings - get all settings */
	get: '/settings',
	/** POST /settings - update settings */
	update: '/settings',
	/** GET /settings/schema - get settings schema */
	schema: '/settings/schema',
	/** POST /settings/upload-image - upload image for settings */
	uploadImage: '/settings/upload-image',
	/** POST /settings/sections/reset - reset section visibility to defaults */
	resetSections: '/settings/sections/reset',
} as const;

// ============================================================================
// TAGS
// @see src/API/Endpoints/TagsEndpoint.php
// ============================================================================

export const TAG_ROUTES = {
	/** GET /tags?entity_type=X - list tags, POST /tags - create */
	list: '/tags',
	/** GET, PUT, DELETE /tags/{id} */
	detail: (id: number) => `/tags/${id}`,
	/** POST /tags/{id}/toggle - toggle is_active */
	toggle: (id: number) => `/tags/${id}/toggle`,
	/** POST /tags/reorder - bulk reorder tags */
	reorder: '/tags/reorder',
} as const;

// ============================================================================
// ENTITY TAGS
// @see src/API/Endpoints/EntityTagsEndpoint.php
// ============================================================================

export const ENTITY_TAG_ROUTES = {
	/** GET /entity-tags/{entityType}/{entityId} - get tags for entity */
	list: (entityType: TagEntityType, entityId: number) => `/entity-tags/${entityType}/${entityId}`,
	/** POST /entity-tags/{entityType}/{entityId} - assign tag (body: { tag_id }) */
	assign: (entityType: TagEntityType, entityId: number) =>
		`/entity-tags/${entityType}/${entityId}`,
	/** PUT /entity-tags/{entityType}/{entityId} - bulk update (body: { tag_ids: [] }) */
	bulkUpdate: (entityType: TagEntityType, entityId: number) =>
		`/entity-tags/${entityType}/${entityId}`,
	/** DELETE /entity-tags/{entityType}/{entityId}/{tagId} - unassign tag */
	unassign: (entityType: TagEntityType, entityId: number, tagId: number) =>
		`/entity-tags/${entityType}/${entityId}/${tagId}`,
} as const;

// ============================================================================
// PERMISSIONS
// @see src/API/Endpoints/PermissionsEndpoint.php
// ============================================================================

export const PERMISSIONS_ROUTES = {
	/** GET /permissions - get all roles with capabilities */
	get: '/permissions',
	/** POST /permissions - update role capabilities */
	update: '/permissions',
	/** POST /permissions/reset - reset to defaults */
	reset: '/permissions/reset',
} as const;

// ============================================================================
// INTEGRATIONS
// @see src/API/Endpoints/IntegrationsEndpoint.php
// ============================================================================

export const INTEGRATION_ROUTES = {
	/** GET /integrations - list available connectors */
	list: '/integrations',
	/** POST /integrations/{connector}/prepare - prepare import (fetch all, consolidate, store in staging) */
	prepare: (connector: string) => `/integrations/${connector}/prepare`,
	/** GET /integrations/{connector}/preview?session_id=xxx - get paginated preview from staging */
	preview: (connector: string) => `/integrations/${connector}/preview`,
	/** POST /integrations/{connector}/import - execute import from staging */
	import: (connector: string) => `/integrations/${connector}/import`,
	/** POST /integrations/{connector}/test - test connection */
	test: (connector: string) => `/integrations/${connector}/test`,
	/** DELETE /integrations/session/{session_id} - cleanup session */
	deleteSession: (sessionId: string) => `/integrations/session/${sessionId}`,
	/** GET /integrations/file_upload/export - export clients as JSON */
	export: (connector: string) => `/integrations/${connector}/export`,
} as const;

// ============================================================================
// GOOGLE CALENDAR
// @see src/API/Endpoints/GoogleCalendarEndpoint.php
// ============================================================================

export const GOOGLE_CALENDAR_ROUTES = {
	/** GET /google-calendar/status */
	status: '/google-calendar/status',
	/** POST /google-calendar/connect/start */
	connectStart: '/google-calendar/connect/start',
	/** GET /google-calendar/oauth/handoff */
	oauthHandoff: '/google-calendar/oauth/handoff',
	/** POST /google-calendar/disconnect */
	disconnect: '/google-calendar/disconnect',
	/** GET /google-calendar/calendars */
	calendars: '/google-calendar/calendars',
	/** POST /google-calendar/target */
	target: '/google-calendar/target',
	/** GET /google-calendar/technician-targets */
	technicianTargets: '/google-calendar/technician-targets',
	/** POST|DELETE /google-calendar/technician-targets/{technicianId} */
	technicianTarget: (technicianId: number) =>
		`/google-calendar/technician-targets/${technicianId}`,
	/** GET /google-calendar/me/status */
	myStatus: '/google-calendar/me/status',
	/** POST /google-calendar/me/connect/start */
	myConnectStart: '/google-calendar/me/connect/start',
	/** POST /google-calendar/me/disconnect */
	myDisconnect: '/google-calendar/me/disconnect',
	/** GET /google-calendar/me/calendars */
	myCalendars: '/google-calendar/me/calendars',
	/** POST /google-calendar/me/target */
	myTarget: '/google-calendar/me/target',
	/** POST /google-calendar/settings */
	settings: '/google-calendar/settings',
	/** POST /google-calendar/sync/backfill */
	backfill: '/google-calendar/sync/backfill',
	/** POST /google-calendar/sync/protocol/{id} */
	protocolSync: (id: number) => `/google-calendar/sync/protocol/${id}`,
	/** POST /google-calendar/sync/check-drift */
	checkDrift: '/google-calendar/sync/check-drift',
	/** GET /google-calendar/sync/errors */
	errors: '/google-calendar/sync/errors',
} as const;

// ============================================================================
// ROLES
// @see src/API/Endpoints/RolesEndpoint.php
// ============================================================================

export const ROLE_ROUTES = {
	/** GET /roles - list all available DDD roles */
	list: '/roles',
} as const;

// ============================================================================
// DOCUMENTS (Polymorphic - all entity types)
// @see src/API/Endpoints/DocumentsEndpoint.php
// ============================================================================

import type { DocumentEntityType } from '@/types';

export const DOCUMENT_ROUTES = {
	/** GET /documents - list all documents with filters */
	list: '/documents',
	/** GET /documents/types - get document types and entity types (SSOT from PHP) */
	types: '/documents/types',
	/** GET /documents/{entityType}/{entityId} - list documents for entity */
	byEntity: (entityType: DocumentEntityType, entityId: number) =>
		`/documents/${entityType}/${entityId}`,
	/** POST /documents/{entityType}/{entityId} - upload document to entity */
	upload: (entityType: DocumentEntityType, entityId: number) =>
		`/documents/${entityType}/${entityId}`,
	/** GET /documents/{id} - get single document */
	detail: (id: number) => `/documents/${id}`,
	/** PUT /documents/{id} - update document metadata */
	update: (id: number) => `/documents/${id}`,
	/** POST /documents/{id}/file - replace document file */
	replaceFile: (id: number) => `/documents/${id}/file`,
	/** DELETE /documents/{id} - delete document */
	delete: (id: number) => `/documents/${id}`,
} as const;

// Backward compatible alias for material documents
export const MATERIAL_DOCUMENT_ROUTES = {
	list: (materialId: number) => DOCUMENT_ROUTES.byEntity('material', materialId),
	upload: (materialId: number) => DOCUMENT_ROUTES.upload('material', materialId),
	update: (id: number) => DOCUMENT_ROUTES.update(id),
	delete: (id: number) => DOCUMENT_ROUTES.delete(id),
} as const;

// ============================================================================
// SITES (Prevádzky)
// @see src/API/Endpoints/SitesEndpoint.php
// ============================================================================

export const SITE_ROUTES = {
	/** GET /sites/{id} - get site detail with stats */
	detail: (id: number) => `/sites/${id}`,
	/** GET /sites/{id}/documents - get documents assigned to site */
	documents: (id: number) => `/sites/${id}/documents`,
	/** GET /sites/{id}/protocols - get protocols executed at site */
	protocols: (id: number) => `/sites/${id}/protocols`,
} as const;

// ============================================================================
// PORTAL DOCUMENTS (Client Portal)
// @see src/API/Endpoints/PortalDocumentsEndpoint.php
// ============================================================================

export const PORTAL_DOCUMENT_ROUTES = {
	/** GET /portal/documents - grouped by material, legacy for dashboard card */
	list: '/portal/documents',
	/** GET /portal/documents/list - flat paginated list of ALL portal-visible docs (material + client + protocol) */
	listPaginated: '/portal/documents/list',
	/** GET /portal/documents/summary - quick summary for dashboard */
	summary: '/portal/documents/summary',
} as const;

// ============================================================================
// PORTAL SITES (Client Portal - Site/Address Detail)
// @see src/API/Endpoints/PortalDocumentsEndpoint.php
// ============================================================================

export const PORTAL_SITE_ROUTES = {
	/** GET /portal/sites - list all sites user has access to */
	list: '/portal/sites',
	/** GET /portal/sites/{id} - get site detail with stats */
	detail: (id: number) => `/portal/sites/${id}`,
	/** GET /portal/sites/{id}/documents - get documents assigned to site (portal visible only) */
	documents: (id: number) => `/portal/sites/${id}/documents`,
	/** GET /portal/sites/{id}/protocols - get protocols executed at site */
	protocols: (id: number) => `/portal/sites/${id}/protocols`,
} as const;

// ============================================================================
// SECURE DOCUMENT DOWNLOAD
// @see src/Services/ProtocolFileHandler.php
// ============================================================================

/**
 * Generates secure download URL for documents
 *
 * Documents are served via ProtocolFileHandler using ?ddd_document_file={id}
 * This provides authentication and visibility checks before serving the file.
 *
 * @param documentId - Document ID from dttm_ddd_documents table
 * @returns Full URL for document download
 */
export const getSecureDocumentUrl = (documentId: number): string => {
	// URL is relative to site root, ProtocolFileHandler handles the query param
	return `${window.location.origin}/?ddd_document_file=${documentId}`;
};

// ============================================================================
// EMAILIT (Domain management + test emails)
// @see src/API/Endpoints/EmailitEndpoint.php
// ============================================================================

export const EMAILIT_ROUTES = {
	/** POST /emailit/domain - register domain, GET - get domain info, DELETE - remove domain */
	domain: '/emailit/domain',
	/** POST /emailit/domain/verify - verify DNS records */
	verifyDomain: '/emailit/domain/verify',
	/** POST /emailit/test - send test email via Emailit */
	testEmail: '/emailit/test',
	/** POST /smtp/test - send test email via SMTP */
	smtpTestEmail: '/smtp/test',
} as const;

// ============================================================================
// SUBSCRIPTION
// @see src/API/Endpoints/SubscriptionEndpoint.php
// ============================================================================

export const SUBSCRIPTION_ROUTES = {
	/** GET /subscription - get current subscription info */
	get: '/subscription',
	/** POST /subscription/refresh - force re-check against EDD server */
	refresh: '/subscription/refresh',
	/** POST /subscription/accept-tos - accept updated Terms of Service */
	acceptTos: '/subscription/accept-tos',
	/** POST /subscription/billing-link - get magic link URL to license portal */
	billingLink: '/subscription/billing-link',
	/** POST /subscription/renewal - cancel or turn back on automatic renewal (company administrator) */
	renewal: '/subscription/renewal',
} as const;

// ============================================================================
// EXPORT
// @see src/API/Endpoints/ExportEndpoint.php
// ============================================================================

export const EXPORT_ROUTES = {
	/** POST /export/full - generate full data export ZIP */
	generate: '/export/full',
} as const;

// ============================================================================
// STATS
// @see src/API/Endpoints/StatsEndpoint.php
// ============================================================================

export const STATS_ROUTES = {
	/** GET /stats/summary - main dashboard summary (protocols, clients, technicians) */
	summary: '/stats/summary',
	/** GET /stats/protocols-over-time - protocols count by date for bar chart */
	protocolsOverTime: '/stats/protocols-over-time',
	/** GET /stats/work-types - work types distribution for pie chart */
	workTypes: '/stats/work-types',
	/** GET /stats/top-clients - top 5 clients by protocol count */
	topClients: '/stats/top-clients',
	/** GET /stats/technicians - technician performance */
	technicians: '/stats/technicians',
	/** GET /stats/materials-usage - top 10 materials by protocol count */
	materialsUsage: '/stats/materials-usage',
	/** GET /stats/materials-trend - top 3 materials usage over time */
	materialsTrend: '/stats/materials-trend',
	/** GET /stats/installation-types - installation types distribution */
	installationTypes: '/stats/installation-types',
} as const;

// ============================================================================
// AGGREGATED EXPORT
// ============================================================================

/**
 * All API routes grouped by domain
 *
 * Usage:
 * ```typescript
 * import { API_ROUTES } from '@/lib/api-routes';
 *
 * // Static routes
 * this.request(API_ROUTES.protocols.list);
 *
 * // Dynamic routes
 * this.request(API_ROUTES.protocols.detail(123));
 * this.request(API_ROUTES.codebooks.list('work-types'));
 * ```
 */
/**
 * Invoice routes (fakturačný modul — DESIGN §5.1, PLAN 1C.1)
 *
 * Never cached by the service worker (sw-routes.ts) and never persisted (invoices-cache.ts).
 */
export const INVOICE_ROUTES = {
	/** GET /invoices - list with filters */
	list: '/invoices',
	/** POST /invoices - create draft for a protocol ({protocol_id}) */
	create: '/invoices',
	/** GET /invoices/{id} - detail with items */
	detail: (id: number) => `/invoices/${id}`,
	/** PUT /invoices/{id} - update draft (optimistic lock via expected_updated_at) */
	update: (id: number) => `/invoices/${id}`,
	/** DELETE /invoices/{id} - soft-delete draft */
	delete: (id: number) => `/invoices/${id}`,
	/** POST /invoices/{id}/issue - issue (number, lock) */
	issue: (id: number) => `/invoices/${id}/issue`,
	/** POST /invoices/{id}/issue?dry_run=1 - pre-flight validation only */
	issueDryRun: (id: number) => `/invoices/${id}/issue?dry_run=1`,
	/** POST /invoices/{id}/credit-note - create credit-note draft ({reason}) */
	creditNote: (id: number) => `/invoices/${id}/credit-note`,
	/** POST /invoices/{id}/refresh - rebuild items from the protocol snapshot */
	refresh: (id: number) => `/invoices/${id}/refresh`,
	/** POST /invoices/{id}/send - export to SuperFaktúra (PLAN 1D.1) */
	send: (id: number) => `/invoices/${id}/send`,
	/** POST /protocols/{id}/invoice - get-or-create prefilled draft (protocol must be sent) */
	forProtocol: (protocolId: number) => `/protocols/${protocolId}/invoice`,
} as const;

export const API_ROUTES = {
	protocols: PROTOCOL_ROUTES,
	clients: CLIENT_ROUTES,
	clientAddresses: CLIENT_ADDRESS_ROUTES,
	formData: FORM_DATA_ROUTES,
	pdf: PDF_ROUTES,
	photos: PHOTO_ROUTES,
	codebooks: CODEBOOK_ROUTES,
	recommendations: RECOMMENDATION_ROUTES,
	users: USER_ROUTES,
	clientUsers: CLIENT_USER_ROUTES,
	activityLogs: ACTIVITY_LOG_ROUTES,
	syncLog: SYNC_LOG_ROUTES,
	feedback: FEEDBACK_ROUTES,
	system: SYSTEM_ROUTES,
	auth: AUTH_ROUTES,
	settings: SETTINGS_ROUTES,
	tags: TAG_ROUTES,
	entityTags: ENTITY_TAG_ROUTES,
	permissions: PERMISSIONS_ROUTES,
	integrations: INTEGRATION_ROUTES,
	googleCalendar: GOOGLE_CALENDAR_ROUTES,
	roles: ROLE_ROUTES,
	subscription: SUBSCRIPTION_ROUTES,
	export: EXPORT_ROUTES,
	stats: STATS_ROUTES,
	documents: DOCUMENT_ROUTES,
	materialDocuments: MATERIAL_DOCUMENT_ROUTES, // Backward compatible alias
	portalDocuments: PORTAL_DOCUMENT_ROUTES,
	portalSites: PORTAL_SITE_ROUTES,
	sites: SITE_ROUTES,
	emailit: EMAILIT_ROUTES,
	invoices: INVOICE_ROUTES,
} as const;
